import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import SEO from "../../components/SEO";
import { isProductionHost } from "../../utils/isProductionHost";
import { Helmet } from "react-helmet-async";
import {
  Users,
  ShieldCheck,
  Search,
  Activity,
  Accessibility,
  Luggage,
  ChevronLeft,
  ChevronRight,
  Star,
  Circle,
  CheckCircle,
  MessageCircle,
  BadgeCheck,
  PlusSquare,
} from "lucide-react";

// Working Partners logos
import partner1 from "../../assets/partners/img1.webp";
import partner2 from "../../assets/partners/img2.webp";
import partner3 from "../../assets/partners/img3.webp";
import partner4 from "../../assets/partners/img4.webp";
import partner5 from "../../assets/partners/img5.webp";
import partner6 from "../../assets/partners/img6.webp";
import partner7 from "../../assets/partners/img7.webp";
import partner8 from "../../assets/partners/img8.webp";
import partner9 from "../../assets/partners/img9.webp";
import partner10 from "../../assets/partners/img10.webp";
import partner11 from "../../assets/partners/img11.webp";
import partner12 from "../../assets/partners/img12.webp";
import partner13 from "../../assets/partners/img13.webp";
import partner14 from "../../assets/partners/img14.webp";
import partner15 from "../../assets/partners/img15.webp";
import partner16 from "../../assets/partners/img16.webp";
import partner17 from "../../assets/partners/img17.webp";
import partner18 from "../../assets/partners/img18.webp";
import partner19 from "../../assets/partners/img19.webp";
import partner20 from "../../assets/partners/img20.webp";
import partner21 from "../../assets/partners/img21.webp";
import partner22 from "../../assets/partners/img22.webp";
import Testimonials from "../../components/Testimonials";
import ScrollToTopButton from "../../components/ScrollToTopButton";
import img86 from "../../assets/img86.webp";
import img87 from "../../assets/img87.webp";
import BlogSection from "../../components/BlogSection";



function InsuranceCard({ card, animationStyle }) {
  const Icon = card.icon;
  const [isFlipped, setIsFlipped] = useState(false);

  const toggleCard = () => {
    setIsFlipped((current) => !current);
  };

  return (
    <article
      style={animationStyle}
      className="group h-[440px] w-full cursor-pointer [perspective:1200px] sm:h-[430px]"
      role="button"
      tabIndex={0}
      aria-label={`${card.title}. Tap or click to view details.`}
      aria-pressed={isFlipped}
      onClick={toggleCard}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggleCard();
        }
      }}
    >
      <div
        className={`
          relative h-full w-full
          [transform-style:preserve-3d]
          transition-transform duration-700 ease-in-out
          lg:group-hover:[transform:rotateY(180deg)]
          ${isFlipped ? "[transform:rotateY(180deg)]" : ""}
        `}
      >
        {/* FRONT SIDE: TITLE, EXPLANATION AND PRICE */}
        <div className="absolute inset-0 flex h-full w-full flex-col rounded-[22px] bg-gradient-to-br from-[#49628b] to-[#20a7df] p-6 text-center text-white shadow-[0_18px_42px_rgba(73,98,139,0.28)] [backface-visibility:hidden] sm:p-7 sm:text-left">
          <div className="mx-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-white/15 text-white sm:mx-0">
            <Icon size={23} strokeWidth={2.2} aria-hidden="true" />
          </div>

          <h3 className="mt-6 text-2xl font-bold leading-tight">
            {card.title}
          </h3>

          <p className="mt-5 flex-1 text-[14px] leading-6 text-white/90">
            {card.explanation}
          </p>

          <div className="mt-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.13em] text-white/75">
              {card.rateLabel}
            </p>

            <p className="mt-1 text-3xl font-extrabold leading-none text-white">
              {card.rate}
            </p>
          </div>
        </div>

        {/* BACK SIDE: DISCLAIMER AND CTA */}
        <div className="absolute inset-0 flex h-full w-full [transform:rotateY(180deg)] flex-col rounded-[22px] bg-gradient-to-br from-[#49628b] to-[#20a7df] p-6 text-center text-white shadow-[0_18px_42px_rgba(73,98,139,0.28)] [backface-visibility:hidden] sm:p-7 sm:text-left">
          <div className="mx-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-white/15 text-white sm:mx-0">
            <Icon size={23} strokeWidth={2.2} aria-hidden="true" />
          </div>

          <h3 className="mt-6 text-2xl font-bold leading-tight">
            {card.title}
          </h3>

          <p className="mt-5 flex-1 text-[14px] leading-6 text-white/90">
            {card.disclaimer}
          </p>

          <a
            href={card.ctaLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="mt-6 inline-flex min-h-11 w-full items-center justify-center rounded-full bg-white px-5 py-2.5 text-sm font-bold text-[#49628b] shadow-[0_10px_24px_rgba(0,0,0,0.16)] transition hover:-translate-y-0.5 hover:bg-[#E8EEF0] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            Obtenir une soumission
          </a>
        </div>
      </div>
    </article>
  );
}

function Home() {
  const navigate = useNavigate();

  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [isHeroFormActive, setIsHeroFormActive] = useState(false);
  const [heroQuote, setHeroQuote] = useState({
  visaType: "Super Visa",
  insured: "single",
  coverage: "100000",
  startDate: "",
  noOfDays: "365",
  dob1: "",
  dob2: "",
});

  const handleLifeInsuranceCta = () => {
    navigate("/fr/prendre-rendez-vous");
  };

  const handleVisitorSuperVisaCta = () => {
    navigate("/fr/devis/vtc");
  };

  const updateHeroQuote = (field, value) => {
    setHeroQuote((current) => ({ ...current, [field]: value }));
  };

  const addDaysToDate = (dateString, numberOfDays) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day, 12);
    date.setDate(date.getDate() + numberOfDays);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleHeroVisitorQuoteSubmit = (event) => {
    event.preventDefault();

    const applicant = heroQuote.insured === "2-persons" ? "2" : "1";
    const numberOfDays =
      heroQuote.visaType === "Super Visa"
        ? 365
        : Math.max(7, Math.min(365, Number(heroQuote.noOfDays)));
    const endDate = addDaysToDate(heroQuote.startDate, numberOfDays - 1);
    const params = new URLSearchParams({
      frmsub: "1",
      visaType: heroQuote.visaType,
      applicant,
      coverage: heroQuote.coverage,
      startDate: heroQuote.startDate,
      endDate,
      "age-one": heroQuote.dob1,
      mcone: "No",
    });

    if (applicant === "2") {
      params.set("age-two", heroQuote.dob2);
      params.set("mctwo", "No");
    }

    navigate(`/fr/devis/vtc?${params.toString()}`);
  };

  const handleHeroPrevious = () => {
    setActiveHeroSlide((current) => (current === 0 ? 1 : 0));
  };

  const handleHeroNext = () => {
    setActiveHeroSlide((current) => (current === 1 ? 0 : 1));
  };

 useEffect(() => {
  if (isHeroFormActive) return undefined;

  const heroTimer = window.setInterval(() => {
    setActiveHeroSlide((current) => (current === 1 ? 0 : 1));
  }, 8000);

  return () => window.clearInterval(heroTimer);
}, [activeHeroSlide, isHeroFormActive]);

  const pauseHeroForForm = () => setIsHeroFormActive(true);
  const resumeHeroAfterForm = () => setIsHeroFormActive(false);



  const container = "mx-auto w-full max-w-[1380px] px-4 sm:px-6 lg:px-10";

  const homePageSchema = {
  "@context": "https://schema.org",
  "@type": ["InsuranceAgency", "LocalBusiness"],
  "@id": "https://www.einsured.ca/#organization",
  name: "Einsured",
  alternateName: "einsured.ca - Assurance vie | Assurance Super Visa",
  url: "https://www.einsured.ca/",
  logo: "https://www.einsured.ca/logo.png",
  image: "https://www.einsured.ca/logo.png",
  description:
    "Einsured est un cabinet d'assurance de premier plan à Brampton, au Canada, offrant l'assurance vie, l'assurance Super Visa, l'assurance santé, l'assurance voyage et des solutions de placement pour les particuliers et les familles partout au Canada.",
  telephone: "+1-844-440-0128",
  email: "info@einsured.ca",
  address: {
    "@type": "PostalAddress",
    streetAddress: "9300 Goreway Drive, Unit #106",
    addressLocality: "Brampton",
    addressRegion: "ON",
    postalCode: "L6P 4N1",
    addressCountry: "CA",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 43.7579458,
    longitude: -79.6950552,
  },
  areaServed: {
    "@type": "Country",
    name: "Canada",
  },
  serviceArea: {
    "@type": "AdministrativeArea",
    name: "Canada",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    telephone: "+1-844-440-0128",
    email: "info@einsured.ca",
    areaServed: "CA",
    availableLanguage: ["English", "French"],
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "10:00",
    closes: "18:00",
  },
  founder: {
    "@type": "Person",
    name: "Aman Ahuja",
  },
  foundingDate: "2017",
  brand: {
    "@type": "Brand",
    name: "Einsured",
  },
  knowsAbout: [
    "Assurance vie",
    "Assurance Super Visa",
    "Assurance santé",
    "Assurance voyage",
    "Planification des placements",
    "REER",
    "CELI",
    "REEE",
  ],
  makesOffer: [
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance vie" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance Super Visa" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance santé" } },
    { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance voyage" } },
  ],
  hasOfferCatalog: {
  "@type": "OfferCatalog",
  name: "Services d'assurance",
  itemListElement: [
    {
      "@type": "OfferCatalog",
      name: "Assurance vie",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance vie temporaire" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance vie permanente" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance vie universelle" } },
      ],
    },
    {
      "@type": "OfferCatalog",
      name: "Assurance voyage",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance Super Visa" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance visiteur" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance étudiants internationaux" } },
      ],
    },
    {
      "@type": "OfferCatalog",
      name: "Prestations du vivant",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance maladies graves" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Assurance invalidité" } },
      ],
    },
  ],
},
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "282",
    bestRating: "5",
    worstRating: "1",
  },
  sameAs: [
    "https://www.facebook.com/einsured.ca/",
    "https://www.linkedin.com/company/einsured-ca/",
    "https://www.instagram.com/einsured.ca/",
    "https://www.pinterest.com/einsuredca/",
    "https://www.youtube.com/@einsured1546",
    "https://x.com/EinsuredCa",
  ],
};
  const [scrollDir, setScrollDir] = useState("up");
  const lastY = useRef(0);
  const rafRef = useRef(null);

  const [flippedStep, setFlippedStep] = useState(null);

  const toggleStepFlip = (idx) => {
    setFlippedStep((cur) => (cur === idx ? null : idx));
  };

  useEffect(() => {
    lastY.current = window.scrollY || 0;

    const onScroll = () => {
      if (rafRef.current) return;

      rafRef.current = requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        const diff = y - lastY.current;

        if (Math.abs(diff) > 6) {
          setScrollDir(diff > 0 ? "down" : "up");
          lastY.current = y;
        }

        rafRef.current = null;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const useInViewOnce = (threshold = 0.22) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);

    useEffect(() => {
      const el = ref.current;
      if (!el) return;

      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        },
        { threshold },
      );

      io.observe(el);
      return () => io.disconnect();
    }, [threshold]);

    return [ref, inView];
  };

  const ease = "cubic-bezier(0.22, 1, 0.36, 1)";

  const ENTRANCE_DUR_MULT = 1.55;
  const ENTRANCE_DIST_MULT = 1.9;

  const dur = (ms) => Math.round(ms * ENTRANCE_DUR_MULT);
  const dist = (px) => Math.round(px * ENTRANCE_DIST_MULT);

  const scaleFromBoost = (scaleFrom) => {
    const delta = 1 - Number(scaleFrom || 1);
    const boosted = 1 - delta * ENTRANCE_DIST_MULT;
    return Math.max(0.94, boosted);
  };

  const makeFadeUp = (inView, delayMs = 0, y = 16, durMs = 700) => {
    const Y = dist(y);
    const D = dur(durMs);

    return {
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0px)" : `translateY(${Y}px)`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  const makeFadeScale = (
    inView,
    delayMs = 0,
    scaleFrom = 0.985,
    durMs = 750,
  ) => {
    const D = dur(durMs);
    const S = scaleFromBoost(scaleFrom);
    const Y = dist(10);

    return {
      opacity: inView ? 1 : 0,
      transform: inView
        ? "translateY(0px) scale(1)"
        : `translateY(${Y}px) scale(${S})`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  const makeFadeLeft = (inView, delayMs = 0, x = 18, durMs = 700) => {
    const X = dist(x);
    const D = dur(durMs);

    return {
      opacity: inView ? 1 : 0,
      transform: inView ? "translateX(0px)" : `translateX(-${X}px)`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  const makeFadeRight = (inView, delayMs = 0, x = 18, durMs = 700) => {
    const X = dist(x);
    const D = dur(durMs);

    return {
      opacity: inView ? 1 : 0,
      transform: inView ? "translateX(0px)" : `translateX(${X}px)`,
      transition: `opacity ${D}ms ${ease} ${delayMs}ms, transform ${D}ms ${ease} ${delayMs}ms`,
      willChange: "transform, opacity",
    };
  };

  // The hero is above the fold, so it renders visible immediately; waiting
  // for an IntersectionObserver + fade-in would delay Largest Contentful Paint.
  const heroInView = true;
  const [partnersRefSection, partnersInView] = useInViewOnce(0.22);

  const [statsRef, statsInView] = useInViewOnce(0.2);
  const [withoutWithRef, withoutWithInView] = useInViewOnce(0.18);
  const [stepsRef, stepsInView] = useInViewOnce(0.18);
  const [typesRef, typesInView] = useInViewOnce(0.18);
  const [pay20Ref, pay20InView] = useInViewOnce(0.18);
  const [bundleRef, bundleInView] = useInViewOnce(0.18);
  const [connectRef, connectInView] = useInViewOnce(0.18);

  // ------- Working Partners slider -------
  const partnerLogos = React.useMemo(
    () => [
      { src: partner1, alt: "Logo UV Insurance" },
      { src: partner2, alt: "Logo GMS" },
      { src: partner3, alt: "Logo Manulife" },
      { src: partner4, alt: "Logo IA Financial Group" },
      { src: partner5, alt: "Logo Foresters Financial" },
      { src: partner6, alt: "Logo TuGo" },
      { src: partner7, alt: "Logo RBC" },
      { src: partner8, alt: "Logo Empire Life" },
      { src: partner9, alt: "Logo Sun Life" },
      { src: partner10, alt: "Logo Equitable" },
      { src: partner11, alt: "Logo Canada Life" },
      { src: partner12, alt: "Logo Assumption Life" },
      { src: partner13, alt: "Logo The Edge Benefits" },
      { src: partner14, alt: "Logo Travel Insurance Limited" },
      { src: partner15, alt: "Logo Allianz Insurance" },
      { src: partner16, alt: "Logo Secure Travel" },
      { src: partner17, alt: "Logo Destination Travel Group" },
      { src: partner18, alt: "Logo Ingle Assurance" },
      { src: partner19, alt: "Logo Travelance" },
      { src: partner20, alt: "Logo BMO Insurance" },
      { src: partner21, alt: "Logo Specialty Life Insurance" },
      { src: partner22, alt: "Logo Canada Protection Plan" },
    ],
    [],
  );

  const partnersRef = React.useRef(null);
  const [partnersPaused, setPartnersPaused] = React.useState(false);

  const slideNext = React.useCallback(() => {
    const el = partnersRef.current;
    if (!el) return;
    const firstChild = el.firstElementChild;
    if (!firstChild) return;

    const step = firstChild.clientWidth + 24;

    const maxScroll = el.scrollWidth - el.clientWidth;
    const nextLeft = el.scrollLeft + step;

    if (nextLeft >= maxScroll - 2) {
      el.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      el.scrollTo({ left: nextLeft, behavior: "smooth" });
    }
  }, []);

  const slidePrev = React.useCallback(() => {
    const el = partnersRef.current;
    if (!el) return;

    const firstChild = el.firstElementChild;
    if (!firstChild) return;

    const step = firstChild.clientWidth + 24;

    const maxScroll = el.scrollWidth - el.clientWidth;
    let nextLeft = el.scrollLeft - step;

    if (nextLeft <= 0) {
      nextLeft = maxScroll;
    }

    el.scrollTo({ left: nextLeft, behavior: "smooth" });
  }, []);

  React.useEffect(() => {
    if (partnersPaused) return;

    const id = setInterval(() => {
      slideNext();
    }, 3000);

    return () => clearInterval(id);
  }, [slideNext, partnersPaused]);

  const [cwName, setCwName] = useState("");
  const [cwPhone, setCwPhone] = useState("");
  const [cwEmail, setCwEmail] = useState("");
  const [cwMessage, setCwMessage] = useState("");

  const [cwTouched, setCwTouched] = useState({
    name: false,
    phone: false,
    email: false,
    message: false,
  });

  const [cwSubmitState, setCwSubmitState] = useState({
    status: "idle",
    banner: "",
  });

  const isEmailValid = (email) => {
    const v = String(email || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  };

  const isPhoneValid = (phone) => {
    const raw = String(phone || "").trim();
    if (!raw) return false;

    const allowed = /^[0-9+\-\s().]*$/.test(raw);
    if (!allowed) return false;

    // count digits
    const digits = raw.replace(/\D/g, "");

    return digits.length >= 10 && digits.length <= 15;
  };

  const cwErrors = React.useMemo(() => {
    const e = {};

    if (!cwName.trim()) e.name = "Veuillez entrer votre nom.";

    if (!String(cwPhone || "").trim()) {
      e.phone = "Veuillez entrer votre numéro de téléphone.";
    } else if (!isPhoneValid(cwPhone)) {
      e.phone = "Veuillez entrer un numéro de téléphone valide.";
    }

    if (!String(cwEmail || "").trim()) {
      e.email = "Veuillez entrer votre adresse courriel.";
    } else if (!isEmailValid(cwEmail)) {
      e.email = "Veuillez entrer une adresse courriel valide.";
    }

    return e;
  }, [cwName, cwPhone, cwEmail]);

  const hasAnyError = Object.keys(cwErrors).length > 0;

  const handleConnectSubmit = async (e) => {
    e.preventDefault();

    setCwTouched({ name: true, phone: true, email: true, message: true });

    if (hasAnyError) {
      setCwSubmitState({
        status: "error",
        banner: "Un ou plusieurs champs contiennent une erreur. Veuillez vérifier et réessayer.",
      });

      const firstInvalid = document.querySelector("[aria-invalid='true']");
      if (firstInvalid && typeof firstInvalid.scrollIntoView === "function") {
        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
        try {
          firstInvalid.focus({ preventScroll: true });
        } catch {}
      }

      return;
    }

    setCwSubmitState({ status: "submitting", banner: "" });

    try {
      const fd = new FormData();
      fd.append("your_name", String(cwName || "").trim());
      fd.append("your-phone", String(cwPhone || "").trim());
      fd.append("your_email", String(cwEmail || "").trim());
      fd.append("your-message", String(cwMessage || "").trim());

      const res = await fetch(
        `https://backend.einsured.ca/api/addContactLead`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: fd,
        },
      );

      const text = await res.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch {
        // ignore
      }

      if (!res.ok) {
        throw new Error(
          json?.message || text || `Request failed (${res.status})`,
        );
      }

      setCwName("");
      setCwPhone("");
      setCwEmail("");
      setCwMessage("");
      setCwTouched({ name: false, phone: false, email: false, message: false });

      setCwSubmitState({
        status: "success",
        banner: "Merci pour votre message. Il a bien été envoyé.",
      });

      setTimeout(() => {
        setCwSubmitState((s) =>
          s.status === "success" ? { status: "idle", banner: "" } : s,
        );
      }, 4500);
    } catch (err) {
      setCwSubmitState({
        status: "error",
        banner:
          err?.message ||
          "Un ou plusieurs champs contiennent une erreur. Veuillez vérifier et réessayer.",
      });
    }
  };

  const wpAnim = useMemo(() => {
    return {
      h2: {
        ...makeFadeUp(partnersInView, 0, 16, 720),
        animation: partnersInView
          ? `wpPulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
          : "none",
      },
      p: makeFadeUp(partnersInView, 90, 12, 720),
      slider: makeFadeScale(partnersInView, 160, 0.987, 780),
      logo: (idx) => ({
        opacity: partnersInView ? 1 : 0,
        transform: partnersInView
          ? "translateY(0px)"
          : `translateY(${dist(10)}px)`,
        transition: `opacity ${dur(520)}ms ${ease} ${
          220 + idx * 55
        }ms, transform ${dur(520)}ms ${ease} ${220 + idx * 55}ms`,
        willChange: "transform, opacity",
      }),
      arrow: (side) => ({
        opacity: partnersInView ? 1 : 0,
        transform: partnersInView
          ? "translateY(-50%) translateX(0px)"
          : `translateY(-50%) translateX(${side === "left" ? `-${dist(8)}px` : `${dist(8)}px`})`,
        transition: `opacity ${dur(620)}ms ${ease} 230ms, transform ${dur(620)}ms ${ease} 230ms`,
        willChange: "transform, opacity",
      }),
    };
  }, [partnersInView, scrollDir]);



  //  STATS counting animation
  const [statCoverage, setStatCoverage] = useState(0); // to 50
  const [statPolicies, setStatPolicies] = useState(0); // to 10000
  const [statClients, setStatClients] = useState(0); // to 1000
  const statsStartedRef = useRef(false);

  useEffect(() => {
    if (!statsInView) return;
    if (statsStartedRef.current) return;
    statsStartedRef.current = true;

    const durCount = 1200;
    const start = performance.now();

    const animate = (now) => {
      const t = Math.min(1, (now - start) / durCount);
      const eased = 1 - Math.pow(1 - t, 3);

      setStatCoverage(Math.round(50 * eased));
      setStatPolicies(Math.round(10000 * eased));
      setStatClients(Math.round(1000 * eased));

      if (t < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [statsInView]);

  const formatNumber = (n) => {
    try {
      return new Intl.NumberFormat("en-CA").format(Number(n || 0));
    } catch {
      return String(n || 0);
    }
  };

  const personalInsuranceCards = [
  {
    id: "term-life",
    icon: Users,
    title: "Assurance vie temporaire",
    explanation:
      "Obtenez une assurance vie abordable pour une période déterminée, comme 10, 20 ou 30 ans, afin de protéger l’avenir financier de votre famille. Vos bénéficiaires reçoivent une prestation de décès non imposable si vous décédez durant la durée de la police.",
    rateLabel: "Prix :",
    rate: "10,98 $",
    disclaimer:
      "Les prix affichés sont basés sur un homme non-fumeur de 30 ans en bonne santé générale, avec une couverture de 100 000 $ pour une durée de 20 ans. Les taux peuvent varier selon la situation de chacun. Des conditions s’appliquent.",
    ctaLink: "https://compulife.org/website/655/quoter.html",
  },
  {
    id: "permanent-life",
    icon: ShieldCheck,
    title: "Assurance vie permanente",
    explanation:
      "Protégez vos proches avec une couverture à vie tout en accumulant une valeur de rachat au fil du temps. L’assurance vie permanente offre une protection financière durable tout en soutenant vos objectifs financiers à long terme.",
    rateLabel: "Prix :",
    rate: "60,03 $",
    disclaimer:
      "Les prix affichés sont basés sur un homme non-fumeur de 30 ans en bonne santé générale, avec une couverture vie entière de 100 000 $. Les taux peuvent varier selon la situation de chacun. Des conditions s’appliquent.",
    ctaLink: "https://compulife.org/website/655/quoter.html",
  },
  {
    id: "critical-illness",
    icon: Activity,
    title: "Assurance maladies graves",
    explanation:
      "Une maladie grave peut entraîner des dépenses imprévues et un arrêt de travail. L’assurance maladies graves verse une prestation forfaitaire si vous recevez un diagnostic visé par la police, vous permettant de vous concentrer sur votre rétablissement plutôt que sur vos finances.",
    rateLabel: "Prix :",
    rate: "14,03 $",
    disclaimer:
      "Les prix affichés sont basés sur un homme non-fumeur de 30 ans en bonne santé générale, avec une couverture de 25 000 $ pour 25 maladies sur une durée de 20 ans. Les taux peuvent varier selon la situation de chacun. Des conditions s’appliquent.",
    ctaLink: "https://book.einsured.ca/widget/bookings/ei-team",
  },
  {
    id: "disability-insurance",
    icon: Accessibility,
    title: "Assurance invalidité",
    explanation:
      "Une maladie ou une blessure peut nuire à votre capacité de gagner un revenu. L’assurance invalidité peut vous fournir un remplacement de revenu mensuel lorsque vous êtes incapable de travailler en raison d’une invalidité couverte.",
    rateLabel: "Prix :",
    rate: "31,19 $",
    disclaimer:
      "Les prix affichés sont basés sur un homme non-fumeur de 30 ans en bonne santé générale, avec une prestation de 15 $/mois pour une période de prestations de 2 ans. Les taux peuvent varier selon la situation de chacun. Des conditions s’appliquent.",
    ctaLink: "https://book.einsured.ca/widget/bookings/ei-team",
  },
  {
    id: "super-visa-insurance",
    icon: Luggage,
    title: "Assurance Super Visa",
    explanation:
      "Offrez à vos parents ou grands-parents en visite une protection contre les dépenses médicales imprévues au Canada. Choisissez parmi des options de couverture flexibles conçues pour répondre aux exigences du Super Visa et aux besoins de votre famille.",
    rateLabel: "Prix :",
    rate: "71,75 $",
    disclaimer:
      "Les prix affichés sont basés sur un homme non-fumeur de 50 ans sans condition médicale préexistante, avec une couverture de 100 000 $ et une franchise de 1 000 $. Les taux peuvent varier selon la situation de chacun. Des conditions s’appliquent.",
    ctaLink: "https://www.einsured.ca/fr/devis/vtc",
  },
  {
  id: "no-medical-insurance",
  icon: ShieldCheck,
  title: "Assurance sans examen médical",
  explanation:
    "Obtenez une assurance vie sans examen médical traditionnel ni questionnaire médical exhaustif, selon le régime choisi. Une option pratique pour ceux qui recherchent une couverture plus simple et plus accessible.",
  rateLabel: "Prix :",
  rate: "10,98 $",
  disclaimer:
  "Les prix affichés sont basés sur un homme non-fumeur de 30 ans en bonne santé générale, avec une couverture de 100 000 $ pour une durée de 20 ans. Les taux peuvent varier selon la situation de chacun. Des conditions s’appliquent.",
  ctaLink: "https://book.einsured.ca/widget/bookings/ei-team",
},
];

  const ratesDisclaimer =
    "Avis : Les taux indiqués ci-dessus sont fournis à titre indicatif seulement et peuvent changer. Nous offrons diverses options de couverture, et les primes peuvent varier selon l’âge, les antécédents médicaux, le montant de la couverture, la durée, la période de prestations et d’autres facteurs individuels. Les taux réels peuvent être plus élevés ou moins élevés. Des conditions s’appliquent.";


  return (
    <div className="w-full overflow-x-hidden">
      <SEO
        title="Cabinet d’assurance et placements au Canada | einsured.ca"
        description="einsured.ca est un cabinet canadien d’assurance et de placements offrant des conseils fiables et des solutions personnalisées pour planifier votre avenir."
        keywords="Courtage en assurance et en placements, assurance vie canada, assurance voyage, assurance Super Visa"
      />
      {/* Skip on staging/preview hosts so this LocalBusiness schema (address,
          phone, ratings) never gets attributed to the real business there. */}
      {isProductionHost() ? (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(homePageSchema)}
          </script>
        </Helmet>
      ) : null}
     {/* HERO CAROUSEL */}
<section
  className="relative overflow-hidden bg-[#49628b] text-white"
>
  <style>{`
    @keyframes heroBannerSlideIn {
      from {
        opacity: 0;
        transform: translateX(100%);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    .hero-banner-slide-active {
      animation: heroBannerSlideIn 700ms cubic-bezier(0.22, 1, 0.36, 1) both;
    }

    @media (prefers-reduced-motion: reduce) {
      .hero-banner-slide-active {
        animation: none;
      }
    }
  `}</style>
  {/* Existing circular background details */}
  <div
    aria-hidden="true"
    className="pointer-events-none absolute -right-[210px] -top-[300px] h-[780px] w-[780px] rounded-full border border-sky-400/10"
  />
  <div
    aria-hidden="true"
    className="pointer-events-none absolute -right-[120px] -top-[210px] h-[610px] w-[610px] rounded-full border border-sky-400/10"
  />
  <div
    aria-hidden="true"
    className="pointer-events-none absolute -right-[35px] -top-[125px] h-[440px] w-[440px] rounded-full border border-sky-400/10"
  />

  <div className={`${container} relative z-10 overflow-hidden pb-8 pt-16 sm:pb-9 sm:pt-12 lg:pb-10 lg:pt-11`}>
    {/* =========================================================
        BANNER 1 — LIFE INSURANCE
        Existing banner stays the same
    ========================================================= */}
    <div className="grid w-full grid-cols-[minmax(0,1fr)] overflow-hidden">
      <div
        className={`col-start-1 row-start-1 min-w-0 ${
          activeHeroSlide === 0
            ? "visible z-10 hero-banner-slide-active"
            : "hidden pointer-events-none z-0 sm:block sm:invisible"
        }`}
        aria-hidden={activeHeroSlide !== 0}
      >
      <div className="grid h-auto min-w-0 grid-cols-1 items-start gap-5 lg:h-full lg:grid-cols-[minmax(0,1.16fr)_minmax(430px,0.84fr)] lg:gap-12 xl:gap-16">
        {/* LEFT CONTENT */}
        <div className="order-1 mx-auto flex w-full max-w-[720px] flex-col items-center text-center lg:mx-0 lg:items-start lg:text-left">
          <div
            style={makeFadeUp(heroInView, 0, 14, 720)}
            className="mb-5 flex items-center justify-center gap-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#E8EEF0] sm:text-[11px] lg:justify-start"
          >
            <span className="h-[2px] w-8 shrink-0 bg-[#20a7df]" />
            <span>Cabinet de courtage canadien indépendant · Depuis 2017</span>
          </div>

          <h1
            style={makeFadeUp(heroInView, 60, 18, 780)}
            className="font-bold leading-[1.06] tracking-[-0.025em] text-[34px] sm:text-[46px] lg:text-[52px] xl:text-[58px]"
          >
            <span className="block whitespace-nowrap">
              La bonne couverture.
            </span>

            <span className="block whitespace-nowrap text-[#20a7df]">
              Choisie avec expertise
            </span>

            <span className="block whitespace-nowrap">
              pour votre vie.
            </span>
          </h1>

          <div
            style={makeFadeUp(heroInView, 120, 14, 760)}
            className="mt-5 max-w-[625px] text-[15px] leading-7 text-white/80 sm:text-[17px] sm:leading-8"
          >
            <p>
              Nous comparons plus de 15 assureurs canadiens de premier plan
              et vous offrons des conseils clairs et impartiaux pour que vous
              soyez protégé sans payer trop cher.
            </p>

            <p className="mt-1">
              Aucun jargon. Aucune pression.
            </p>
          </div>

          <div
            style={makeFadeUp(heroInView, 175, 12, 720)}
            className="mt-5 flex flex-col items-center lg:items-start"
          >
            <p className="text-sm font-semibold text-white sm:text-base">
              Noté 4,9/5 partout au Canada
            </p>

            <div
              className="mt-2 flex items-center gap-1"
              role="img" aria-label="Noté 4,9 sur 5 partout au Canada"
            >
              {Array.from({ length: 5 }, (_, i) => (
                <Star
                  key={i}
                  size={20}
                  className="fill-yellow-400 text-yellow-400"
                  aria-hidden="true"
                  style={{
                    opacity: heroInView ? 1 : 0,
                    transform: heroInView
                      ? "translateY(0px)"
                      : `translateY(${dist(8)}px)`,
                    transition: `opacity ${dur(520)}ms ${ease} ${
                      220 + i * 60
                    }ms, transform ${dur(520)}ms ${ease} ${
                      220 + i * 60
                    }ms`,
                    willChange: "transform, opacity",
                  }}
                />
              ))}
            </div>
          </div>

          <div
            style={makeFadeUp(heroInView, 240, 10, 700)}
            className="mt-6 flex w-full justify-center sm:w-auto lg:justify-start"
          >
            <button
              type="button"
              onClick={handleLifeInsuranceCta}
              className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#20a7df] px-7 py-3 text-base font-semibold text-[#0a2540] shadow-[0_12px_30px_rgba(8,183,239,0.28)] transition duration-300 hover:-translate-y-0.5 hover:bg-[#20a7df] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#49628b]"
            >
              Soumission d&apos;assurance vie
            </button>
          </div>
        </div>

        {/* MOBILE NAVIGATION — consistently placed directly above the card */}
        <div className="order-2 flex w-full items-center justify-between px-1 sm:hidden">
          <button
            type="button"
            onClick={handleHeroPrevious}
            aria-label="Bannière précédente"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white shadow-sm backdrop-blur-sm transition active:scale-95"
          >
            <ChevronLeft size={22} />
          </button>

          <button
            type="button"
            onClick={handleHeroNext}
            aria-label="Bannière suivante"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white shadow-sm backdrop-blur-sm transition active:scale-95"
          >
            <ChevronRight size={22} />
          </button>
        </div>

        {/* RIGHT CONTENT — EXISTING COMPULIFE CALCULATOR */}
        <div
          className="order-3 w-full lg:order-2"
          style={makeFadeRight(heroInView, 90, 18, 780)}
          onMouseEnter={pauseHeroForForm}
          onMouseLeave={resumeHeroAfterForm}
          onFocusCapture={pauseHeroForForm}
          onBlurCapture={resumeHeroAfterForm}
          onTouchStart={pauseHeroForForm}
        >
          <div className="mx-auto w-full max-w-[500px]">
            <div
              className="
                relative
                h-[480px]
                w-full
                overflow-x-hidden
                overflow-y-auto
                rounded-[18px]
                bg-white
                shadow-[0_20px_50px_rgba(0,0,0,0.32)]
                sm:h-[520px]
                sm:rounded-[20px]
                lg:h-[520px]
              "
            >
              <iframe
                src="https://compulife.org/website/655/quoter.html"
                title="Calculateur de soumission d'assurance vie CompuLife"
                loading="eager"
                referrerPolicy="strict-origin-when-cross-origin"
                className="
                  absolute
                  left-0
                  top-0
                  h-[700px]
                  w-[125%]
                  max-w-none
                  origin-top-left
                  scale-[0.8]
                  border-0
                  bg-white
                  sm:h-[650px]
                  sm:w-[111.12%]
                  sm:scale-90
                  lg:h-[650px]
                  lg:w-full
                  lg:scale-100
                "
              >
                <a href="https://compulife.org/website/655/quoter.html">
                  Ouvrir le calculateur de soumission d&apos;assurance vie CompuLife
                </a>
              </iframe>
            </div>
          </div>
        </div>
      </div>
      </div>

    {/* =========================================================
        BANNER 2 — VISITOR / SUPER VISA
    ========================================================= */}
      <div
        className={`col-start-1 row-start-1 min-w-0 ${
          activeHeroSlide === 1
            ? "visible z-10 hero-banner-slide-active"
            : "hidden pointer-events-none z-0 sm:block sm:invisible"
        }`}
        aria-hidden={activeHeroSlide !== 1}
      >
      <div className="grid h-full min-w-0 grid-cols-1 items-start gap-5 lg:grid-cols-2 lg:gap-12 xl:gap-16">
        {/* LEFT CONTENT */}
        <div className="mx-auto flex w-full max-w-[720px] flex-col items-center text-center lg:mx-0 lg:items-start lg:text-left">
          <h2 className="text-[34px] font-bold leading-[1.1] tracking-[-0.025em] text-white sm:text-[46px] lg:text-[52px]">
            Assurance visiteur et <span className="text-[#20a7df]">Super Visa</span>{" "}
            pour le Canada
          </h2>

         <div className="mt-6 max-w-[680px] text-[15px] leading-7 text-white/80 sm:text-[17px] sm:leading-8">
  <p>
    Comparez les régimes d&apos;assurance visiteurs au Canada auprès de plus
    de 13 assureurs canadiens de premier plan et trouvez la bonne couverture
    pour vos proches.
  </p>

  <p className="mt-1">
    Conseillers agréés. Livraison instantanée de la police. Polices
    remboursables et abordables.
  </p>

 <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-3 text-sm font-semibold text-white/80 lg:flex-nowrap lg:justify-start lg:gap-x-3">
  <div className="flex items-center gap-1.5">
    <ShieldCheck
      size={18}
      className="shrink-0 text-[#20a7df]"
      aria-hidden="true"
    />
    <span>Conseillers agréés</span>
  </div>

  <span
    className="hidden h-3 w-px bg-white/30 sm:block"
    aria-hidden="true"
  />

  <div className="flex items-center gap-1.5">
    <Search
      size={18}
      className="shrink-0 text-[#20a7df]"
      aria-hidden="true"
    />
    <span>Livraison instantanée de la police</span>
  </div>

  <span
    className="hidden h-3 w-px bg-white/30 sm:block"
    aria-hidden="true"
  />

  <div className="flex items-center gap-1.5">
    <Users
      size={18}
      className="shrink-0 text-[#20a7df]"
      aria-hidden="true"
    />
    <span>Polices remboursables et abordables</span>
  </div>
</div>
</div>

<div className="mt-5 flex flex-col items-center lg:items-start">
  <p className="text-sm font-semibold text-white sm:text-base">
    Noté 4,9/5 partout au Canada
  </p>

  <div
    className="mt-2 flex items-center gap-1"
    role="img" aria-label="Noté 4,9 sur 5 partout au Canada"
  >
    {Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        size={20}
        className="fill-yellow-400 text-yellow-400"
        aria-hidden="true"
        style={{
          opacity: heroInView ? 1 : 0,
          transform: heroInView
            ? "translateY(0px)"
            : `translateY(${dist(8)}px)`,
          transition: `opacity ${dur(520)}ms ${ease} ${
            220 + i * 60
          }ms, transform ${dur(520)}ms ${ease} ${
            220 + i * 60
          }ms`,
          willChange: "transform, opacity",
        }}
      />
    ))}
  </div>
</div>

          <div className="mt-6 flex w-full justify-center sm:w-auto lg:justify-start">
            <button
              type="button"
              onClick={handleVisitorSuperVisaCta}
              className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#20a7df] px-7 py-3 text-base font-semibold text-[#0a2540] shadow-[0_12px_30px_rgba(8,183,239,0.28)] transition duration-300 hover:-translate-y-0.5 hover:bg-[#20a7df] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#49628b]"
            >
              Obtenez votre soumission
            </button>
          </div>
        </div>

        {/* MOBILE NAVIGATION — same position and spacing as Banner 1 */}
        <div className="order-2 flex w-full items-center justify-between px-1 sm:hidden">
          <button
            type="button"
            onClick={handleHeroPrevious}
            aria-label="Bannière précédente"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white shadow-sm backdrop-blur-sm transition active:scale-95"
          >
            <ChevronLeft size={22} />
          </button>

          <button
            type="button"
            onClick={handleHeroNext}
            aria-label="Bannière suivante"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-white/10 text-white shadow-sm backdrop-blur-sm transition active:scale-95"
          >
            <ChevronRight size={22} />
          </button>
        </div>

       {/* RIGHT CONTENT — VISITOR / SUPER VISA QUOTE PREVIEW */}
<div
  className="order-3 w-full lg:order-2"
  style={makeFadeRight(heroInView, 90, 18, 780)}
  onMouseEnter={pauseHeroForForm}
  onMouseLeave={resumeHeroAfterForm}
  onFocusCapture={pauseHeroForForm}
  onBlurCapture={resumeHeroAfterForm}
  onTouchStart={pauseHeroForForm}
>
  <div className="mx-auto w-full max-w-[540px]">
    <form
  onSubmit={handleHeroVisitorQuoteSubmit}
  onChange={pauseHeroForForm}
  className={`h-[620px] overflow-y-auto overscroll-contain rounded-[20px] bg-white p-5 text-left text-[#444E59] shadow-[0_20px_50px_rgba(0,0,0,0.28)] sm:h-[520px] sm:p-6 ${
    heroQuote.insured === "single"
      ? "lg:h-auto"
      : "lg:h-[520px]"
  }`}
>
      <div className="mb-5 text-center">
        <h3 className="text-xl font-bold text-[#49628b] sm:text-2xl">
          Soumission Visiteur et Super Visa
        </h3>
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
        {/* SUPER VISA */}
        <div>
          <label className="mb-2 block text-sm font-semibold text-[#444E59]">
            Est-ce pour un Super Visa?
          </label>

          <div className="grid grid-cols-2 overflow-hidden rounded-md border border-gray-300">
            <label className="cursor-pointer">
              <input
                type="radio"
                name="hero_super_visa"
                checked={heroQuote.visaType === "Super Visa"}
                onChange={() => updateHeroQuote("visaType", "Super Visa")}
                className="peer sr-only"
              />

             <span className="flex h-10 items-center justify-center bg-[#E8EEF0] text-sm font-medium text-[#444E59] peer-checked:bg-[#20a7df] peer-checked:text-[#0a2540]">
  Oui
</span>
            </label>

            <label className="cursor-pointer">
              <input
                type="radio"
                name="hero_super_visa"
                checked={heroQuote.visaType === "Visitor Visa"}
                onChange={() => updateHeroQuote("visaType", "Visitor Visa")}
                className="peer sr-only"
              />

              <span className="flex h-10 items-center justify-center bg-[#E8EEF0] text-sm font-medium text-[#444E59] peer-checked:bg-[#20a7df] peer-checked:text-[#0a2540]">
                Non
              </span>
            </label>
          </div>
        </div>

        {/* INSURED */}
        <div>
          <label className="mb-2 block text-sm font-semibold text-[#444E59]">
            Assuré(e)?
          </label>

          <div className="grid grid-cols-2 overflow-hidden rounded-md border border-gray-300">
            <label className="cursor-pointer">
              <input
                type="radio"
                name="hero_insured"
                checked={heroQuote.insured === "single"}
                onChange={() => updateHeroQuote("insured", "single")}
                className="peer sr-only"
              />

           <span className="flex h-10 items-center justify-center bg-[#E8EEF0] text-sm font-medium text-[#444E59] peer-checked:bg-[#20a7df] peer-checked:text-[#0a2540]">
  Seul(e)
</span>
            </label>

            <label className="cursor-pointer">
              <input
                type="radio"
                name="hero_insured"
                checked={heroQuote.insured === "2-persons"}
                onChange={() => updateHeroQuote("insured", "2-persons")}
                className="peer sr-only"
              />

              <span className="flex h-10 items-center justify-center bg-[#E8EEF0] text-sm font-medium text-[#444E59] peer-checked:bg-[#20a7df] peer-checked:text-[#0a2540]">
                2 personnes
              </span>
            </label>
          </div>
        </div>

        {/* COVERAGE */}
        <div>
          <label
            htmlFor="heroCoverage"
            className="mb-2 block text-sm font-semibold text-[#444E59]"
          >
            Couverture (CAD)
          </label>

          <select
            id="heroCoverage"
            value={heroQuote.coverage}
            onChange={(event) => updateHeroQuote("coverage", event.target.value)}
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-[#444E59] outline-none transition focus:border-[#20a7df] focus:ring-1 focus:ring-[#20a7df]"
          >
            <option value="100000">100 000 $</option>
            <option value="150000">150 000 $</option>
            <option value="200000">200 000 $</option>
          </select>
        </div>

        {/* START DATE */}
        <div>
          <label
            htmlFor="heroStartDate"
            className="mb-2 block text-sm font-semibold text-[#444E59]"
          >
            Date de début
          </label>

          <input
            id="heroStartDate"
            type="date"
            required
            value={heroQuote.startDate}
            min={new Date().toISOString().split("T")[0]}
            onChange={(event) => updateHeroQuote("startDate", event.target.value)}
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-[#444E59] outline-none transition focus:border-[#20a7df] focus:ring-1 focus:ring-[#20a7df]"
          />
        </div>

        {/* NUMBER OF DAYS */}
        <div>
          <label
            htmlFor="heroDays"
            className="mb-2 block text-sm font-semibold text-[#444E59]"
          >
            Nombre de jours
          </label>

          <input
            id="heroDays"
            type="number"
            min="7"
            max="365"
            required
            disabled={heroQuote.visaType === "Super Visa"}
            value={heroQuote.visaType === "Super Visa" ? "365" : heroQuote.noOfDays}
            onChange={(event) => updateHeroQuote("noOfDays", event.target.value)}
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-[#444E59] outline-none transition focus:border-[#20a7df] focus:ring-1 focus:ring-[#20a7df]"
          />
        </div>

        {/* DATE OF BIRTH */}
        <div>
          <label
            htmlFor="heroDob"
            className="mb-2 block text-sm font-semibold text-[#444E59]"
          >
            Date de naissance
          </label>

          <input
            id="heroDob"
            type="date"
            required
            value={heroQuote.dob1}
            max={new Date().toISOString().split("T")[0]}
            onChange={(event) => updateHeroQuote("dob1", event.target.value)}
            className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-[#444E59] outline-none transition focus:border-[#20a7df] focus:ring-1 focus:ring-[#20a7df]"
          />
        </div>

        {heroQuote.insured === "2-persons" && (
          <div>
            <label
              htmlFor="heroDob2"
              className="mb-2 block text-sm font-semibold text-[#444E59]"
            >
              Date de naissance – personne 2
            </label>

            <input
              id="heroDob2"
              type="date"
              required
              value={heroQuote.dob2}
              max={new Date().toISOString().split("T")[0]}
              onChange={(event) => updateHeroQuote("dob2", event.target.value)}
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-[#444E59] outline-none transition focus:border-[#20a7df] focus:ring-1 focus:ring-[#20a7df]"
            />
          </div>
        )}
      </div>

      <button
        type="submit"
        className="mt-5 flex min-h-12 w-full items-center justify-center rounded-md bg-[#49628b] px-6 py-3 text-base font-bold text-white shadow-[0_10px_24px_rgba(73,98,139,0.22)] transition duration-300 hover:bg-[#3e5579] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20a7df] focus-visible:ring-offset-2"
      >
        Calculer
      </button>

      <p className="mt-3 text-center text-xs text-[#444E59]/90">
        Poursuivez pour voir les soumissions d&apos;assurance disponibles
      </p>
    </form>
  </div>
</div>
      </div>
      </div>
    </div>
  </div>

  {/* CAROUSEL PREVIOUS BUTTON */}
  <button
    type="button"
    onClick={handleHeroPrevious}
    aria-label="Bannière précédente"
    className="absolute left-3 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/20 sm:flex"
  >
    <ChevronLeft size={24} />
  </button>

  {/* CAROUSEL NEXT BUTTON */}
  <button
    type="button"
    onClick={handleHeroNext}
    aria-label="Bannière suivante"
    className="absolute right-3 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white backdrop-blur-sm transition hover:bg-white/20 sm:flex"
  >
    <ChevronRight size={24} />
  </button>

  {/* MOBILE / DESKTOP SLIDE INDICATORS */}
  <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
    <button
      type="button"
      onClick={() => setActiveHeroSlide(0)}
      aria-label="Afficher la bannière Assurance vie"
      className={`h-2.5 rounded-full transition-all duration-300 ${
        activeHeroSlide === 0
          ? "w-8 bg-[#20a7df]"
          : "w-2.5 bg-white/50"
      }`}
    />

    <button
      type="button"
      onClick={() => setActiveHeroSlide(1)}
      aria-label="Afficher la bannière Assurance visiteur et Super Visa"
      className={`h-2.5 rounded-full transition-all duration-300 ${
        activeHeroSlide === 1
          ? "w-8 bg-[#20a7df]"
          : "w-2.5 bg-white/50"
      }`}
    />
  </div>
</section>
      {/* WORKING PARTNERS */}
      <section ref={partnersRefSection} className="py-14 md:py-20 bg-[#E8EEF0]">
        <style>{`
          @keyframes wpPulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.12); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div className={`${container} text-center`}>
          <h2
            key={`${partnersInView}-${scrollDir}`}
            style={wpAnim.h2}
            className="text-2xl sm:text-3xl md:text-5xl font-bold text-[#49628b] mb-3"
          >
            Nos partenaires
          </h2>
          <p style={wpAnim.p} className="text-gray-600 max-w-3xl mx-auto">
            einsured.ca collabore avec les compagnies les plus réputées,
            offrant des solutions d&apos;assurance depuis plus de 100 ans
          </p>

          {/* Slider */}
          <div
            style={wpAnim.slider}
            className="relative mt-10"
            onMouseEnter={() => setPartnersPaused(true)}
            onMouseLeave={() => setPartnersPaused(false)}
          >
            {/* Left arrow */}
            <button
              type="button"
              onClick={slidePrev}
              aria-label="Partenaires précédents"
              style={wpAnim.arrow("left")}
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10
               w-10 h-10 items-center justify-center rounded-full
               bg-white shadow-md border border-gray-200
               hover:shadow-lg hover:scale-105 transition"
            >
              <ChevronLeft className="w-5 h-5 text-[#49628b]" />
            </button>

            <div className="px-14 sm:px-16 md:px-20 lg:px-24 overflow-hidden">
              <div
                ref={partnersRef}
                className="flex items-center gap-6 sm:gap-10 md:gap-12 lg:gap-16
                 overflow-x-auto sm:overflow-hidden
                 scroll-smooth
                 snap-x snap-mandatory sm:snap-none
                 touch-pan-x
                 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {partnerLogos.map((logo, idx) => (
                  <div
                    key={idx}
                    style={wpAnim.logo(idx)}
                    className="flex-shrink-0 flex items-center justify-center
     w-32 sm:w-40 md:w-48 lg:w-52 snap-center"
                  >
                    <img
                      src={logo.src}
                      alt={logo.alt}
                      loading="lazy"
                      width={200}
                      height={100}
                      className="w-auto max-h-12 sm:max-h-14 md:max-h-16 object-contain
       transition-transform duration-300 ease-out
       hover:scale-[1.06] hover:-translate-y-[2px]"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right arrow */}
            <button
              type="button"
              onClick={slideNext}
              aria-label="Partenaires suivants"
              style={wpAnim.arrow("right")}
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10
               w-10 h-10 items-center justify-center rounded-full
               bg-white shadow-md border border-gray-200
               hover:shadow-lg hover:scale-105 transition"
            >
              <ChevronRight className="w-5 h-5 text-[#49628b]" />
            </button>
          </div>
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section className="bg-white py-14 sm:py-16 md:py-20">
        <div className={container}>
          <div className="mx-auto max-w-4xl text-center">
            <div className="flex items-center justify-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-[#20a7df] sm:text-sm">
              <span className="h-[2px] w-8 bg-[#20a7df]" />
              <span>Pourquoi c'est important</span>
              <span className="h-[2px] w-8 bg-[#20a7df]" />
            </div>

            <h2 className="mt-4 text-3xl font-bold leading-tight tracking-[-0.02em] text-[#49628b] sm:text-4xl md:text-5xl">
              Le risque n&apos;est pas hypothétique — il est simplement réparti
              de façon inégale au fil d&apos;une vie.
            </h2>

            <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-[#444E59] sm:text-lg sm:leading-8">
              La plupart des gens commencent à magasiner une couverture
              seulement après qu&apos;un événement touche quelqu&apos;un qu&apos;ils
              connaissent. Les chiffres montrent qu&apos;il vaut mieux s&apos;y
              pencher plus tôt.
            </p>
          </div>

          <div className="mx-auto mt-10 grid max-w-[1280px] grid-cols-1 gap-5 text-center sm:mt-12 sm:grid-cols-2 sm:text-left lg:grid-cols-4 lg:gap-6">
            <article className="flex h-full flex-col rounded-[18px] border border-[#49628b]/10 bg-[#E8EEF0] p-6 shadow-[0_12px_30px_rgba(73,98,139,0.10)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(73,98,139,0.16)] sm:p-7">
              <p className="text-4xl font-bold tracking-tight text-[#20a7df] sm:text-5xl">
                1 sur 3
              </p>
              <p className="mt-5 flex-1 text-[15px] leading-7 text-[#444E59] sm:text-base">
  Des Canadiens en âge de travailler deviendront invalides et
  incapables de travailler avant l&apos;âge de 65 ans — un scénario
  plus probable que le décès durant ces mêmes années.
</p>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-[#49628b]">
                RBC Insurance
              </p>
            </article>

            <article className="flex h-full flex-col rounded-[18px] border border-[#49628b]/10 bg-[#E8EEF0] p-6 shadow-[0_12px_30px_rgba(73,98,139,0.10)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(73,98,139,0.16)] sm:p-7">
              <p className="text-4xl font-bold tracking-tight text-[#20a7df] sm:text-5xl">
                2 sur 5
              </p>
              <p className="mt-5 flex-1 text-[15px] leading-7 text-[#444E59] sm:text-base">
                Canadiens recevront un diagnostic de cancer à un moment de
                leur vie, et environ 1 sur 4 en mourra.
              </p>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-[#49628b]">
                Société canadienne du cancer
              </p>
            </article>

            <article className="flex h-full flex-col rounded-[18px] border border-[#49628b]/10 bg-[#E8EEF0] p-6 shadow-[0_12px_30px_rgba(73,98,139,0.10)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(73,98,139,0.16)] sm:p-7">
              <p className="text-4xl font-bold tracking-tight text-[#20a7df] sm:text-5xl">
                ~10 %
              </p>
              <p className="mt-5 flex-1 text-[15px] leading-7 text-[#444E59] sm:text-base">
                Des Canadiens détiennent une assurance invalidité, même si
                près de 3 sur 10 affirment que leurs économies s&apos;épuiseraient
                dans les six mois suivant un problème de santé majeur.
              </p>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-[#49628b]">
                RBC Insurance
              </p>
            </article>

            <article className="flex h-full flex-col rounded-[18px] border border-[#49628b]/10 bg-[#E8EEF0] p-6 shadow-[0_12px_30px_rgba(73,98,139,0.10)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_18px_38px_rgba(73,98,139,0.16)] sm:p-7">
              <p className="text-4xl font-bold tracking-tight text-[#20a7df] sm:text-5xl">
                8,4 M
              </p>
              <p className="mt-5 flex-1 text-[15px] leading-7 text-[#444E59] sm:text-base">
  D&apos;adultes canadiens, soit environ 31 % de la population,
  affirment avoir besoin d&apos;assurance vie ou d&apos;une couverture
  plus importante que celle qu&apos;ils possèdent actuellement.
</p>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-[#49628b]">
                LIMRA &amp; Life Happens, 2023 Barometer Study
              </p>
            </article>
          </div>

          <div className="mx-auto mt-8 max-w-[1280px] rounded-[18px] border-l-4 border-[#20a7df] bg-[#49628b]/[0.06] px-5 py-5 text-center sm:mt-10 sm:px-7 sm:py-6 sm:text-left">
            <p className="text-[15px] leading-7 text-[#444E59] sm:text-base sm:leading-8">
              Les avantages sociaux offerts au travail sont un bon départ, mais
              pas un plan complet — la couverture est habituellement plafonnée
              à une ou deux fois le salaire et prend généralement fin le jour
              où vous quittez votre emploi. Une assurance maladies graves,
              invalidité ou vie que vous détenez vous-même vous suit, peu
              importe la situation.
            </p>
          </div>
        </div>
      </section>

      {/* STATS SECTION */}
      <section
        ref={statsRef}
        className="py-16 md:py-30 bg-gradient-to-b from-[#20a7df] to-[#49628b] text-white mt-4"
      >
        <div className={container}>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 items-start">
              {/* Stat 1 */}
              <div
                style={makeFadeUp(statsInView, 0, 16, 780)}
                className="text-center"
              >
                <div className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight drop-shadow">
                  {statCoverage}+ millions
                </div>
                <div className="mt-3 text-lg sm:text-xl font-medium opacity-95 drop-shadow">
                  Couverture totale vendue
                </div>
              </div>

              {/* Stat 2 */}
              <div
                style={makeFadeUp(statsInView, 120, 16, 780)}
                className="text-center"
              >
                <div className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight drop-shadow">
                  {formatNumber(statPolicies)}+
                </div>
                <div className="mt-3 text-lg sm:text-xl font-medium opacity-95 drop-shadow">
                  Polices vendues
                </div>
              </div>

              {/* Stat 3 */}
              <div
                style={makeFadeUp(statsInView, 220, 16, 780)}
                className="text-center"
              >
                <div className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight drop-shadow">
                  {formatNumber(statClients)}+
                </div>
                <div className="mt-3 text-lg sm:text-xl font-medium opacity-95 drop-shadow">
                  Clients servis
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              opacity: statsInView ? 1 : 0,
              transform: statsInView ? "scaleX(1)" : "scaleX(0.92)",
              transformOrigin: "center",
              transition: `opacity ${dur(700)}ms ${ease} 260ms, transform ${dur(700)}ms ${ease} 260ms`,
            }}
          />
        </div>
      </section>

      {/* WITHOUT US vs WITH US */}
      <section ref={withoutWithRef} className="py-16 md:py-20 bg-white">
        <div className={container}>
          <div className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
            {/* LEFT: Without Us  */}
            <div className="relative w-full">
              {/* Top tab */}
              <div
                className="relative z-10 rounded-[36px] bg-white shadow-xl border border-gray-200
                        px-8 py-6 text-center"
                style={makeFadeLeft(withoutWithInView, 40, 28, 1100)}
              >
                <h2 className="text-4xl sm:text-5xl font-bold text-[#49628b] tracking-tight">
                  Sans nous
                </h2>
              </div>

              <div className="mt-6 space-y-6">
                {[
                  "Primes trop élevées",
                  "Choix limités",
                  "Économies manquées",
                  "Couverture floue",
                  "Soutien limité",
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={makeFadeLeft(
                      withoutWithInView,
                      220 + idx * 140,
                      26,
                      1150,
                    )}
                    className="bg-white shadow-lg border border-gray-200 rounded-md
                         px-6 py-5 flex items-center gap-3"
                  >
                    <Circle className="text-red-500 flex-shrink-0" size={22} />
                    <p className="text-lg sm:text-xl font-medium text-gray-900">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT: With Us  */}
            <div className="relative w-full">
              {/* Top tab */}
              <div
                className="relative z-10 rounded-[36px] bg-sky-500 shadow-xl
                        px-8 py-6 text-center"
                style={makeFadeRight(withoutWithInView, 40, 28, 1100)}
              >
                <h2 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
                  Avec nous
                </h2>
              </div>

              <div className="mt-6 space-y-6">
                {[
                  "Primes abordables",
                  "Solutions modernes et choix de régimes",
                  "Économies grâce aux régimes de placement",
                  "Processus entièrement numérique et simplifié",
                  "Soutien à la clientèle dédié",
                ].map((item, idx) => (
                  <div
                    key={idx}
                    style={makeFadeRight(
                      withoutWithInView,
                      220 + idx * 140,
                      26,
                      1150,
                    )}
                    className="shadow-xl rounded-md px-6 py-5 flex items-center gap-3
                         bg-gradient-to-b from-[#20a7df] to-[#49628b]"
                  >
                    <CheckCircle
                      className="text-green-400 mt-1 flex-shrink-0"
                      size={22}
                    />
                    <p className="text-lg sm:text-xl font-semibold text-white">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GET COVERED IN 3 EASY STEPS + CALENDAR */}
      <section ref={stepsRef} className="bg-white">
        <style>{`
          @keyframes stepsPulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.08); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div
          className="bg-[#20a7df] py-10 md:py-12"
          style={makeFadeScale(stepsInView, 0, 0.992, 780)}
        >
          <div className={`${container} text-center`}>
            <h2
              key={`${stepsInView}-${scrollDir}`}
              style={{
                ...makeFadeUp(stepsInView, 0, 16, 720),
                animation: stepsInView
                  ? `stepsPulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
                  : "none",
              }}
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-white drop-shadow"
            >
              Obtenez une couverture en 3 étapes faciles
            </h2>
          </div>
        </div>


        {/* Content */}
<div className={`${container} py-8 md:py-10`}>
          <div className="grid grid-cols-1 lg:grid-cols-[1.08fr_0.92fr] gap-8 lg:gap-10 items-start lg:items-stretch">
            <div className="space-y-10 lg:space-y-10 lg:flex lg:flex-col lg:justify-center lg:h-full">
              {/* Steps */}
              <div className="space-y-5">
                {/* STEP 1 */}
                <div
                  style={makeFadeLeft(stepsInView, 120, 18, 720)}
                  className="group [perspective:1200px] cursor-pointer select-none touch-manipulation"
                  role="button"
                  tabIndex={0}
                  aria-pressed={flippedStep === 1}
                  onClick={() => toggleStepFlip(1)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleStepFlip(1);
                    }
                  }}
                >
                  <div className="relative min-h-[110px] sm:h-[110px] lg:h-[116px]">
                    <div
                      className="absolute inset-0 transition-transform duration-1000 ease-in-out
                           [transform-style:preserve-3d]
                           group-hover:[transform:rotateY(180deg)]"
                      style={{
                        transform:
                          flippedStep === 1 ? "rotateY(180deg)" : undefined,
                      }}
                    >
                      {/* FRONT */}
                      <div className="absolute inset-0 flex items-center gap-0 [backface-visibility:hidden]">
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 1
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center gap-3
                                  bg-gradient-to-b from-[#20a7df] to-[#49628b]
                                  rounded-r-md shadow-[0_12px_30px_rgba(0,0,0,0.22)]"
                        >
                          <MessageCircle className="text-white" size={22} />
                          <span className="text-lg sm:text-xl font-semibold text-white">
                            Communiquez avec nous
                          </span>
                        </div>
                      </div>

                      {/* BACK */}
                      <div
                        className="absolute inset-0 flex items-center [transform:rotateY(180deg)]
                             [backface-visibility:hidden]"
                      >
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 1
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center
                                  bg-white rounded-r-md border border-gray-200
                                  shadow-[0_12px_30px_rgba(0,0,0,0.18)] px-6"
                        >
                          <p className="text-base sm:text-xl font-medium text-[#2a3a57] text-center">
                            Contactez-nous ou prenez rendez-vous — nous vous
                            guiderons personnellement
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* STEP 2 */}
                <div
                  style={makeFadeLeft(stepsInView, 210, 18, 720)}
                  className="group [perspective:1200px] cursor-pointer select-none touch-manipulation"
                  role="button"
                  tabIndex={0}
                  aria-pressed={flippedStep === 2}
                  onClick={() => toggleStepFlip(2)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleStepFlip(2);
                    }
                  }}
                >
                  <div className="relative min-h-[110px] sm:h-[110px] lg:h-[116px]">
                    <div
                      className="absolute inset-0 transition-transform duration-1000 ease-in-out
                           [transform-style:preserve-3d]
                           group-hover:[transform:rotateY(180deg)]"
                      style={{
                        transform:
                          flippedStep === 2 ? "rotateY(180deg)" : undefined,
                      }}
                    >
                      {/* FRONT */}
                      <div className="absolute inset-0 flex items-center [backface-visibility:hidden]">
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 2
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center gap-3
                                  bg-gradient-to-b from-[#20a7df] to-[#49628b]
                                  rounded-r-md shadow-[0_12px_30px_rgba(0,0,0,0.22)]"
                        >
                          <BadgeCheck className="text-white" size={22} />
                          <span className="text-lg sm:text-xl font-semibold text-white">
                            Choisissez votre régime
                          </span>
                        </div>
                      </div>

                      {/* BACK */}
                      <div
                        className="absolute inset-0 flex items-center [transform:rotateY(180deg)]
                             [backface-visibility:hidden]"
                      >
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 2
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center
                                  bg-white rounded-r-md border border-gray-200
                                  shadow-[0_12px_30px_rgba(0,0,0,0.18)] px-6"
                        >
                          <p className="text-base sm:text-xl font-medium text-[#2a3a57] text-center">
                            Nous vous présentons des solutions personnalisées et
                            vous choisissez celle qui vous convient le mieux
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* STEP 3 */}
                <div
                  style={makeFadeLeft(stepsInView, 300, 18, 720)}
                  className="group [perspective:1200px] cursor-pointer select-none touch-manipulation"
                  role="button"
                  tabIndex={0}
                  aria-pressed={flippedStep === 3}
                  onClick={() => toggleStepFlip(3)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleStepFlip(3);
                    }
                  }}
                >
                  <div className="relative min-h-[110px] sm:h-[110px] lg:h-[116px]">
                    <div
                      className="absolute inset-0 transition-transform duration-1000 ease-in-out
                           [transform-style:preserve-3d]
                           group-hover:[transform:rotateY(180deg)]"
                      style={{
                        transform:
                          flippedStep === 3 ? "rotateY(180deg)" : undefined,
                      }}
                    >
                      {/* FRONT */}
                      <div className="absolute inset-0 flex items-center [backface-visibility:hidden]">
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 3
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center gap-3
                                  bg-gradient-to-b from-[#20a7df] to-[#49628b]
                                  rounded-r-md shadow-[0_12px_30px_rgba(0,0,0,0.22)]"
                        >
                          <PlusSquare className="text-white" size={22} />
                          <span className="text-lg sm:text-xl font-semibold text-white">
                            Obtenez votre approbation
                          </span>
                        </div>
                      </div>

                      {/* BACK */}
                      <div
                        className="absolute inset-0 flex items-center [transform:rotateY(180deg)]
                             [backface-visibility:hidden]"
                      >
                        <div
                          className="w-[150px] sm:w-[175px] lg:w-[185px] h-full flex items-center justify-center
                                  rounded-l-full bg-white shadow-[0_10px_24px_rgba(0,0,0,0.18)]
                                  border border-gray-200"
                        >
                          <span className="text-base sm:text-lg font-medium tracking-wide text-black">
                            ÉTAPE 3
                          </span>
                        </div>

                        <div
                          className="flex-1 h-full flex items-center justify-center
                                  bg-white rounded-r-md border border-gray-200
                                  shadow-[0_12px_30px_rgba(0,0,0,0.18)] px-6"
                        >
                          <p className="text-base sm:text-xl font-medium text-[#2a3a57] text-center">
                            Nous soumettons votre demande, et votre couverture
                            est activée rapidement
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>


            </div>
{/* RIGHT: Calendar */}
<div
  style={makeFadeRight(stepsInView, 180, 18, 760)}
  className="bg-white rounded-[32px] shadow-[0_18px_40px_rgba(0,0,0,0.18)] w-full p-3 sm:p-4"
>
  <div className="rounded-[24px] overflow-hidden border border-gray-200 bg-white">
    <div className="relative h-[560px] sm:h-[600px] md:h-[620px] lg:h-[640px] overflow-y-auto">
      <iframe
        src="https://book.einsured.ca/widget/bookings/ei-team"
        title="Évaluation de 10 minutes einsured.ca"
        loading="lazy"
        className="w-full border-0 h-[980px]"
      />
    </div>
  </div>
</div>
          </div>
        </div>
      </section>

      {/* PERSONAL INSURANCE */}
      <section ref={typesRef} className="bg-[#E8EEF0] py-16 md:py-24">
        <div className={container}>
          {/* Personal insurance heading */}
          <div
            style={makeFadeUp(typesInView, 0, 16, 760)}
            className="mx-auto max-w-[760px] text-center"
          >
            <div className="flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-[#20a7df] sm:text-xs">
              <span className="h-px w-7 bg-[#20a7df]" />
              <span>Ce que nous couvrons</span>
              <span className="h-px w-7 bg-[#20a7df]" />
            </div>

            <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-[-0.025em] text-[#49628b] sm:text-4xl md:text-5xl">
              L&apos;assurance personnelle pour
              <br className="hidden sm:block" /> chaque étape de la vie
            </h2>

            <p className="mx-auto mt-4 max-w-[620px] text-sm leading-6 text-[#444E59] sm:text-base sm:leading-7">
              De votre première hypothèque à la planification de la retraite,
              nous trouvons le type de couverture qui correspond à votre
              situation actuelle.
            </p>
          </div>

          {/* 6 insurance cards: 3 in the first row and 3 in the second row */}
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:mt-14 lg:grid-cols-3 lg:gap-7">
            {personalInsuranceCards.map((card, index) => (
              <InsuranceCard
                key={card.id}
                card={card}
                animationStyle={{
                  opacity: typesInView ? 1 : 0,
                  transform: typesInView
                    ? "translateY(0px)"
                    : `translateY(${dist(14)}px)`,
                  transition: `opacity ${dur(620)}ms ${ease} ${
                    120 + index * 85
                  }ms, transform ${dur(620)}ms ${ease} ${
                    120 + index * 85
                  }ms`,
                  willChange: "transform, opacity",
                }}
              />
            ))}
          </div>

          <p
            style={makeFadeUp(typesInView, 720, 10, 680)}
            className="mx-auto mt-10 max-w-[980px] text-center text-sm italic leading-6 text-[#444E59] sm:text-base"
          >
            {ratesDisclaimer}
          </p>
        </div>
      </section>

      {/* PAY 20 YEARS WHOLE LIFE POLICY SECTION */}
      <section ref={pay20Ref} className=" py-10 md:py-14">
        <style>{`
          @keyframes pay20Pulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.08); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div className={container}>
          <div
            style={makeFadeScale(pay20InView, 0, 0.988, 780)}
            className="w-full max-w-[1120px] mx-auto
                 grid grid-cols-1 lg:grid-cols-2
                 rounded-[14px] overflow-hidden
                 shadow-[0_14px_34px_rgba(0,0,0,0.16)]"
          >
            {/* LEFT */}
            <div className="bg-gradient-to-r from-[#20a7df] to-[#49628b] text-white px-7 sm:px-9 md:px-10 py-10 md:py-10">
              <h2
                key={`${pay20InView}-${scrollDir}`}
                style={{
                  ...makeFadeUp(pay20InView, 90, 10, 720),
                  animation: pay20InView
                    ? `pay20Pulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
                    : "none",
                }}
                className="text-4xl sm:text-5xl md:text-4xl font-bold leading-tight drop-shadow"
              >
                Police vie entière <br /> payable en 20 ans
              </h2>

              <p
                style={makeFadeUp(pay20InView, 150, 10, 720)}
                className="mt-5 max-w-[520px] text-base sm:text-lg md:text-xl leading-relaxed text-white/95"
              >
                Après 20 ans, votre police est entièrement acquittée, vous
                offrant sécurité financière, protection garantie et
                tranquillité d&apos;esprit pour le reste de votre vie.
              </p>

              {/* Highlight box */}
              <div
                style={makeFadeUp(pay20InView, 210, 10, 720)}
                className="mt-8 max-w-[560px] bg-[#2f86bb]/35 border border-white/20 rounded-[10px]
                     shadow-[0_10px_22px_rgba(0,0,0,0.18)]
                     px-6 sm:px-7 py-5"
              >
                <p className="text-base sm:text-lg md:text-xl leading-relaxed text-white/95">
                  Plus de 5 600 clients ont choisi cette police, ne payant des
                  primes que pendant 20 ans pour profiter d&apos;une couverture
                  d&apos;assurance vie à vie
                </p>
              </div>

              {/* Button */}
              <div
                style={makeFadeUp(pay20InView, 270, 10, 720)}
                className="mt-9 flex justify-center lg:justify-start"
              >
                <button
                  type="button"
                  className="w-full sm:w-[320px]
         py-3.5 rounded-full
         border-2 border-sky-300/70
         text-white font-semibold text-lg
         bg-white/10
         shadow-[0_12px_26px_rgba(0,0,0,0.20)]
         transition duration-300
         hover:bg-white/14 hover:border-sky-200/90"
                  style={{
                    willChange: "transform",
                    transition: `transform ${dur(420)}ms ${ease}, background-color 300ms, border-color 300ms`,
                  }}
                  onClick={() =>
                    window.open(
                      "https://book.einsured.ca/widget/bookings/ei-team",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-2px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0px)")
                  }
                >
                  Demander une soumission
                </button>
              </div>
            </div>

            {/* RIGHT */}
            <div
              style={makeFadeRight(pay20InView, 120, 16, 720)}
              className="relative min-h-[280px] sm:min-h-[320px] lg:min-h-full"
            >
              <img
                src={img86}
                alt="Famille examinant une police d'assurance"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                style={{
                  transform: pay20InView ? "scale(1)" : "scale(1.02)",
                  transition: `transform ${dur(900)}ms ${ease}`,
                  willChange: "transform",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <div
        style={makeFadeUp(
          bundleInView || connectInView || pay20InView,
          0,
          10,
          720,
        )}
      >
        <Testimonials limit={12} autoPlay={true} intervalMs={4500} />
      </div>

      <ScrollToTopButton showAfterPx={420} />

      {/* BUNDLE TERM + WHOLE LIFE SECTION */}
      <section ref={bundleRef} className="bg-[#E8EEF0] py-12 md:py-16">
        <style>{`
          @keyframes bundlePulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.08); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div className={container}>
          <div
            style={makeFadeScale(bundleInView, 0, 0.988, 800)}
            className="w-full max-w-[1180px] mx-auto
                 grid grid-cols-1 lg:grid-cols-2
                 rounded-[18px] overflow-hidden
                 shadow-[0_18px_45px_rgba(0,0,0,0.18)]"
          >
            {/* LEFT */}
            <div
              style={makeFadeLeft(bundleInView, 90, 16, 740)}
              className="relative min-h-[320px] sm:min-h-[420px] lg:min-h-full"
            >
              <img
                src={img87}
                alt="Famille examinant des options d'assurance"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                style={{
                  transform: bundleInView ? "scale(1)" : "scale(1.02)",
                  transition: `transform ${dur(900)}ms ${ease}`,
                  willChange: "transform",
                }}
              />
            </div>

            {/* RIGHT */}
            <div className="bg-gradient-to-r from-[#20a7df] to-[#49628b] text-white px-8 sm:px-10 md:px-12 py-10 md:py-12">
              <h2
                key={`${bundleInView}-${scrollDir}`}
                style={{
                  ...makeFadeUp(bundleInView, 120, 10, 720),
                  animation: bundleInView
                    ? `bundlePulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
                    : "none",
                }}
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight drop-shadow"
              >
                Combinez temporaire <br /> et vie entière
              </h2>

              <p
                style={makeFadeUp(bundleInView, 190, 10, 720)}
                className="mt-5 max-w-[560px] text-base sm:text-lg md:text-xl leading-relaxed text-white/95"
              >
                L&apos;assurance temporaire offre une couverture élevée à
                faible coût durant vos années les plus exigeantes
                financièrement, tandis que l&apos;assurance vie entière assure
                une sécurité à vie et accumule une valeur de rachat au fil du
                temps. Ensemble, elles créent un équilibre judicieux entre
                abordabilité et tranquillité d&apos;esprit durable pour vous et
                votre famille.
              </p>

              {/* Highlight box */}
              <div
                style={makeFadeUp(bundleInView, 250, 10, 720)}
                className="mt-8 max-w-[620px]
                     bg-[#2f86bb]/35 border border-white/20 rounded-[10px]
                     shadow-[0_12px_30px_rgba(0,0,0,0.20)]
                     px-6 sm:px-7 py-6"
              >
                <p className="text-base sm:text-lg md:text-xl leading-relaxed text-white/95">
                  La plupart de nos clients choisissent cette option pour une
                  flexibilité et une abordabilité accrues.
                </p>
              </div>

              {/* Our Clients + Progress */}
              <div
                style={makeFadeUp(bundleInView, 310, 10, 720)}
                className="mt-8"
              >
                <div className="text-lg font-semibold text-white/95">
                  Nos clients
                </div>

                <div className="mt-3 w-full max-w-[760px]">
                  <div className="w-full bg-white/90 rounded-[6px] overflow-hidden shadow-[0_10px_24px_rgba(0,0,0,0.18)] border border-white/30">
                    <div
                      className="bg-[#20a7df] h-[34px] sm:h-[32px] px-6 flex items-center justify-between"
                      style={{
                        width: bundleInView ? "90%" : "0%",
                        transition: `width ${dur(900)}ms ${ease} 320ms`,
                        willChange: "width",
                      }}
                    >
                      <span className="text-white font-semibold text-sm sm:text-[15px]">
                        Approuvé
                      </span>

                      <span className="text-white font-semibold text-sm sm:text-[15px]">
                        90 %
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Button */}
              <div
                style={makeFadeUp(bundleInView, 380, 10, 720)}
                className="mt-10 flex justify-center lg:justify-center"
              >
                <button
                  type="button"
                  className="w-full sm:w-[320px]
       py-4 rounded-full
       border-2 border-sky-300/70
       text-white font-semibold text-lg
       bg-white/10
       shadow-[0_14px_35px_rgba(0,0,0,0.22)]
       transition duration-[350ms]
       hover:bg-white/14 hover:border-sky-200/90"
                  style={{
                    willChange: "transform",
                    transition: `transform ${dur(420)}ms ${ease}, background-color 350ms, border-color 350ms`,
                  }}
                  onClick={() =>
                    window.open(
                      "https://book.einsured.ca/widget/bookings/ei-team",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-2px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0px)")
                  }
                >
                  Obtenir l&apos;approbation
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONNECT WITH US SECTION */}
      <section ref={connectRef} className="bg-white pt-14 md:pt-0 pb-[50px]">
        <style>{`
          @keyframes connectPulse {
            0%   { transform: translateY(0px) scale(1); }
            50%  { transform: translateY(0px) scale(1.08); }
            100% { transform: translateY(0px) scale(1); }
          }
        `}</style>

        <div
          className="bg-gradient-to-b from-[#20a7df] to-[#49628b] py-12 md:py-14"
          style={makeFadeScale(connectInView, 0, 0.992, 800)}
        >
          <div className={`${container} text-center`}>
            <h2
              key={`${connectInView}-${scrollDir}`}
              style={{
                ...makeFadeUp(connectInView, 0, 16, 720),
                animation: connectInView
                  ? `connectPulse ${dur(420)}ms cubic-bezier(0.22, 1, 0.36, 1)`
                  : "none",
              }}
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-white drop-shadow"
            >
              Communiquez avec nous
            </h2>
          </div>
        </div>

        <div className={`${container} pt-12 md:pt-16 pb-0`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            {/* LEFT: FORM */}
            <div
              style={makeFadeLeft(connectInView, 120, 18, 760)}
              className="max-w-[520px] mx-auto lg:mx-0 w-full"
            >
              <form
                onSubmit={handleConnectSubmit}
                noValidate
                className="space-y-7"
              >
                {/* Name */}
                <div style={makeFadeUp(connectInView, 160, 10, 720)}>
                  <label htmlFor="cw_name" className="block text-lg font-semibold text-black mb-2">
                    Votre nom <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="cw_name"
                    name="cw_name"
                    autoComplete="name"
                    maxLength={32}
                    value={cwName}
                    onChange={(e) => {
                      setCwName(e.target.value);
                      if (cwSubmitState.status !== "idle") {
                        setCwSubmitState({ status: "idle", banner: "" });
                      }
                    }}
                    onBlur={() => setCwTouched((t) => ({ ...t, name: true }))}
                    placeholder="Votre nom"
                    aria-invalid={cwTouched.name && !!cwErrors.name}
                    aria-describedby="cw_name_error"
                    className={
                      "w-full h-[52px] px-5 rounded-[4px] border text-lg text-gray-700 placeholder:text-gray-400 " +
                      "focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 " +
                      (cwTouched.name && cwErrors.name
                        ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                        : "border-gray-200")
                    }
                  />
                  {cwTouched.name && cwErrors.name ? (
                    <p
                      id="cw_name_error"
                      className="mt-2 text-red-600 text-base"
                    >
                      {cwErrors.name}
                    </p>
                  ) : null}
                </div>

                {/* Phone */}
                <div style={makeFadeUp(connectInView, 210, 10, 720)}>
                  <label htmlFor="cw_phone" className="block text-lg font-semibold text-black mb-2">
                    Votre téléphone <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="tel"
                    id="cw_phone"
                    name="cw_phone"
                    autoComplete="tel"
                    inputMode="tel"
                    value={cwPhone}
                    onChange={(e) => {
                      setCwPhone(e.target.value);
                      if (cwSubmitState.status !== "idle") {
                        setCwSubmitState({ status: "idle", banner: "" });
                      }
                    }}
                    onBlur={() => setCwTouched((t) => ({ ...t, phone: true }))}
                    placeholder="Votre téléphone"
                    aria-invalid={cwTouched.phone && !!cwErrors.phone}
                    aria-describedby="cw_phone_error"
                    className={
                      "w-full h-[52px] px-5 rounded-[4px] border text-lg text-gray-700 placeholder:text-gray-400 " +
                      "focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 " +
                      (cwTouched.phone && cwErrors.phone
                        ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                        : "border-gray-200")
                    }
                  />
                  {cwTouched.phone && cwErrors.phone ? (
                    <p
                      id="cw_phone_error"
                      className="mt-2 text-red-600 text-base"
                    >
                      {cwErrors.phone}
                    </p>
                  ) : null}
                </div>

                {/* Email */}
                <div style={makeFadeUp(connectInView, 260, 10, 720)}>
                  <label htmlFor="cw_email" className="block text-lg font-semibold text-black mb-2">
                    Votre courriel <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="cw_email"
                    name="cw_email"
                    autoComplete="email"
                    inputMode="email"
                    value={cwEmail}
                    onChange={(e) => {
                      setCwEmail(e.target.value);
                      if (cwSubmitState.status !== "idle") {
                        setCwSubmitState({ status: "idle", banner: "" });
                      }
                    }}
                    onBlur={() => setCwTouched((t) => ({ ...t, email: true }))}
                    placeholder="Votre courriel"
                    aria-invalid={cwTouched.email && !!cwErrors.email}
                    aria-describedby="cw_email_error"
                    className={
                      "w-full h-[52px] px-5 rounded-[4px] border text-lg text-gray-700 placeholder:text-gray-400 " +
                      "focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300 " +
                      (cwTouched.email && cwErrors.email
                        ? "border-red-500 focus:ring-red-200 focus:border-red-500"
                        : "border-gray-200")
                    }
                  />
                  {cwTouched.email && cwErrors.email ? (
                    <p
                      id="cw_email_error"
                      className="mt-2 text-red-600 text-base"
                    >
                      {cwErrors.email}
                    </p>
                  ) : null}
                </div>

                {/* Message */}
                <div style={makeFadeUp(connectInView, 310, 10, 720)}>
                  <label htmlFor="cw_message" className="block text-lg font-semibold text-black mb-2">
                    Votre message
                  </label>
                  <textarea
                    id="cw_message"
                    name="cw_message"
                    autoComplete="off"
                    value={cwMessage}
                    onChange={(e) => setCwMessage(e.target.value)}
                    onBlur={() =>
                      setCwTouched((t) => ({ ...t, message: true }))
                    }
                    placeholder=""
                    rows={6}
                    className="w-full px-5 py-4 rounded-[4px] border border-[#2a3a57]
                               text-lg text-gray-700 placeholder:text-gray-400
                               focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-sky-300"
                  />
                </div>

                {/* Send button */}
                <button
                  type="submit"
                  disabled={cwSubmitState.status === "submitting"}
                  className="mt-2 inline-flex items-center justify-center
                             bg-[#20a7df] hover:bg-sky-600 text-white font-bold
                             w-[220px] h-[62px] rounded-full text-xl
                             shadow-[0_12px_26px_rgba(0,0,0,0.16)]
                             transition disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    ...makeFadeUp(connectInView, 360, 10, 720),
                    willChange: "transform",
                    transition: `opacity ${dur(720)}ms ${ease} 360ms, transform ${dur(520)}ms ${ease}, background-color 300ms`,
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-2px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0px)")
                  }
                >
                  {cwSubmitState.status === "submitting"
                    ? "ENVOI EN COURS..."
                    : "ENVOYER"}
                </button>

                {/* Error banner */}
                {cwSubmitState.status === "error" && cwSubmitState.banner ? (
                  <div
                    style={makeFadeUp(connectInView, 420, 10, 720)}
                    className="mt-6 border-2 border-[#f59e0b] bg-white
                               px-6 py-4 text-lg text-gray-800"
                  >
                    {cwSubmitState.banner}
                  </div>
                ) : null}

                {/* Success banner */}
                {cwSubmitState.status === "success" && cwSubmitState.banner ? (
                  <div
                    style={makeFadeUp(connectInView, 420, 10, 720)}
                    className="mt-6 border-2 border-[#22c55e] bg-white
                               px-6 py-4 text-lg text-gray-800"
                  >
                    {cwSubmitState.banner}
                  </div>
                ) : null}
              </form>
            </div>

            {/* RIGHT: DETAILS + MAP */}
            <div
              style={makeFadeRight(connectInView, 120, 18, 760)}
              className="w-full max-w-[820px] mx-auto lg:mx-0"
            >
              {/* Opening Hours box */}
              <div
                style={makeFadeUp(connectInView, 150, 10, 720)}
                className="bg-gradient-to-b from-[#20a7df] to-[#49628b]
                           text-white text-center
                           py-6 px-6
                           shadow-[0_14px_34px_rgba(0,0,0,0.22)]
                           rounded-[2px]"
              >
                <div className="text-3xl sm:text-4xl font-bold drop-shadow">
                  Heures d&apos;ouverture
                </div>
              </div>

              <div
                style={makeFadeUp(connectInView, 210, 10, 720)}
                className="mt-6 text-xl text-gray-800"
              >
                <span className="font-semibold">Lundi au vendredi</span> –
                de 10 h à 18 h
              </div>

              {/* Address box */}
              <div
                style={makeFadeUp(connectInView, 260, 10, 720)}
                className="mt-8 bg-gradient-to-b from-[#20a7df] to-[#49628b]
                           text-white text-center
                           py-6 px-6
                           shadow-[0_14px_34px_rgba(0,0,0,0.22)]
                           rounded-[2px]"
              >
                <div className="text-3xl sm:text-4xl font-bold drop-shadow">
                  Adresse
                </div>
              </div>

              <div
                style={makeFadeUp(connectInView, 310, 10, 720)}
                className="mt-6 space-y-4 text-xl text-gray-800"
              >
                <div>
                  <span className="font-semibold">Ontario :</span> 9300 Goreway
                  Drive, Unit #106, Brampton, ON L6P 4N1
                </div>
                <div>
                  <span className="font-semibold">Alberta :</span> 9426 51 Avenue
                  NW,Suite 201, Edmonton, T6E 5A6
                </div>
              </div>

              {/* Map */}
              <div
                style={makeFadeScale(connectInView, 360, 0.99, 780)}
                className="mt-8 w-full rounded-[6px] overflow-hidden shadow-[0_16px_36px_rgba(0,0,0,0.18)] border border-gray-200"
              >
                <iframe
                  title="Carte Google – bureau de l'Ontario"
                  src="https://www.google.com/maps?q=9300%20Goreway%20Dr%20%23106%2C%20Brampton%2C%20ON%20L6P%204N1&z=12&output=embed"
                  className="w-full h-[360px] md:h-[380px] border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
      <BlogSection title="Derniers articles de blogue" mode="related" relatedCount={3} />
    </div>
  );
}

export default Home;
